import React from 'react';
// VALUE imports via the dist subpath, never the '@valutten/shared-schemas'
// barrel (it pulls Node crypto into the browser bundle). Types are erased.
import {
  CLASSIFICATION_DIMENSIONS,
  CLASSIFICATION_DIMENSION_QUESTIONS,
  optionList,
} from '@valutten/shared-schemas/dist/leadClassification';
import type {
  ClassificationOptions,
  ClassificationOption,
  LeadClassification,
} from '@valutten/shared-schemas';

/**
 * The five lead classification questions as form fields (loan purpose,
 * property use, borrower situation, loan structure, loan amount). Used by the manual lead
 * form and the lead detail panel. Shows only active options, in order; a
 * dimension the service offers no options for is not asked. With no options
 * at all (the service asks nothing) renders nothing.
 *
 * Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.4, §8.
 */

export interface LeadClassificationFieldsProps {
  options: ClassificationOptions | null;
  value: LeadClassification;
  onChange: (next: LeadClassification) => void;
  disabled?: boolean;
  /** Prefix for input ids, so two instances on one page do not collide. */
  idPrefix?: string;
}

function activeSorted(list: readonly ClassificationOption[]): ClassificationOption[] {
  return list.filter((o) => o.active !== false).slice().sort((a, b) => a.order - b.order);
}

export const LeadClassificationFields: React.FC<LeadClassificationFieldsProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  idPrefix = 'lead-classification',
}) => {
  if (!options) return null;
  const asked = CLASSIFICATION_DIMENSIONS.filter((d) => activeSorted(optionList(options, d)).length > 0);
  if (asked.length === 0) return null;

  return (
    <div className="space-y-3" data-testid="lead-classification-fields">
      {asked.map((dim) => {
        const opts = activeSorted(optionList(options, dim));
        const inputId = `${idPrefix}-${dim}`;
        if (dim === 'borrowerSituation') {
          return (
            <fieldset key={dim} disabled={disabled}>
              <legend className="block text-xs font-medium text-gray-400 mb-1">
                {CLASSIFICATION_DIMENSION_QUESTIONS[dim]}
              </legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {opts.map((o) => {
                  const checked = value.borrowerSituations.includes(o.id);
                  return (
                    <label key={o.id} className="inline-flex items-center gap-1.5 text-sm text-gray-200">
                      <input
                        type="checkbox"
                        className="accent-blue-500 w-3.5 h-3.5"
                        checked={checked}
                        onChange={(e) => {
                          const set = new Set(value.borrowerSituations);
                          if (e.target.checked) set.add(o.id);
                          else set.delete(o.id);
                          // Keep option order so the stored value is stable.
                          onChange({ ...value, borrowerSituations: opts.map((x) => x.id).filter((id) => set.has(id)) });
                        }}
                      />
                      {o.label}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        }
        const current = value[dim] ?? null;
        return (
          <div key={dim}>
            <label htmlFor={inputId} className="block text-xs font-medium text-gray-400 mb-1">
              {CLASSIFICATION_DIMENSION_QUESTIONS[dim]}
            </label>
            <select
              id={inputId}
              disabled={disabled}
              value={current ?? ''}
              onChange={(e) => onChange({ ...value, [dim]: e.target.value || null })}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
            >
              <option value="">Not answered</option>
              {opts.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
              {/* A stored value whose option was since removed stays visible. */}
              {current && !opts.some((o) => o.id === current) && (
                <option value={current}>{current}</option>
              )}
            </select>
          </div>
        );
      })}
    </div>
  );
};

export default LeadClassificationFields;
